import { httpCall } from '../../../common/util';
import { FALLBACK_COUNTRIES } from '../constants';

const countryCache = {
  property: null,
  mailing: null,
};

export function clearCountryCache() {
  countryCache.property = null;
  countryCache.mailing = null;
}


function extractItems(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  return (
    response.data ||
    response.suggestion ||
    response.suggestions ||
    response.countries ||
    response.items ||
    response.result ||
    []
  );
}

function normalizeCountryOption(item) {
  if (!item) return null;
  if (typeof item === 'string') return { value: item, label: item };

  const value =
    item.value ??
    item.code ??
    item.country_code ??
    item.countryCode ??
    item.country ??
    item.key ??
    item.id ??
    null;

  const label =
    item.label ??
    item.name ??
    item.country_name ??
    item.countryName ??
    item.display_name ??
    item.text ??
    value;

  if (!value) return null;
  return { value: String(value), label: String(label) };
}

export async function fetchSectionCountries(section) {
  if (countryCache[section]) {
    return countryCache[section];
  }

  const endpoint = section === 'property' ? '/1.0/pl/countries' : '/1.0/ma/countries';

  try {
    const response = await httpCall({ url: endpoint, method: 'GET' });

    const items = extractItems(response);
    const normalized = items.map(normalizeCountryOption).filter(Boolean);

    if (!normalized.length) {
      console.warn(`[addressApi] ${section} countries returned empty after normalization. Using fallback.`);
      return FALLBACK_COUNTRIES;
    }

    countryCache[section] = normalized;
    return normalized;
  } catch (error) {
    console.error(`[addressApi] Failed to fetch ${section} countries:`, error);
    return FALLBACK_COUNTRIES;
  }
}


function normalizeStateOption(item) {
  if (!item) return null;
  if (typeof item === 'string') return { value: item, label: item };
  const code = item.state || item.code || item.value || item.key || null;
  const label = item.state_full_name || item.name || item.label || code;
  if (!code && !label) return null;
  const val = label || code;
  return { value: String(val), label: String(label || val) };
}

export async function searchStates({ q = '', country }) {
  if (!country) return [];

  try {
    const response = await httpCall({
      url: `/1.0/address/states?q=${encodeURIComponent(q)}&country=${encodeURIComponent(country)}`,
      method: 'GET',
    });

    const items = extractItems(response);
    return items.map(normalizeStateOption).filter(Boolean);
  } catch (error) {
    console.error('[addressApi] Failed to search states:', error);
    return [];
  }
}


export async function searchCounties({ q = '', state }) {
  if (!state) return [];

  try {
    const response = await httpCall({
      url: `/1.0/counties/${encodeURIComponent(state)}`,
      method: 'GET',
    });

    const items = response?.data || [];
    const filtered = q
      ? items.filter((i) => String(i.county || '').toLowerCase().includes(String(q).toLowerCase()))
      : items;

    return filtered
      .filter((i) => i && i.county)
      .map((i) => ({ value: i.county, label: i.county }));
  } catch (error) {
    console.error('[addressApi] Failed to search counties:', error);
    return [];
  }
}


export async function searchCities({ q = '', county }) {
  if (!county) return [];

  try {
    const response = await httpCall({
      url: `/1.0/cities?q=${encodeURIComponent(q)}&county=${encodeURIComponent(county)}`,
      method: 'GET',
    });

    const items = response?.suggestion || response?.data || [];
    return items
      .filter((i) => i && i.city)
      .map((i) => ({ value: i.city, label: i.city }));
  } catch (error) {
    console.error('[addressApi] Failed to search cities:', error);
    return [];
  }
}


export async function lookupZip({ zip, country }) {
  if (!zip || !country) return null;

  try {
    const response = await httpCall({
      url: `/1.0/locations/zip?q=${encodeURIComponent(zip)}&c=${encodeURIComponent(country)}`,
      method: 'GET',
    });

    const suggestions = response?.suggestion || response?.suggestions || [];
    if (!suggestions.length) return null;

    const first = suggestions[0];
    return {
      state: first.state || null,
      stateFullName: first.state_full_name || null,
      county: first.county || null,
      city: first.city || null,
      country: first.country || null,
      zipcode: first.zipcode || null,
      timeZoneId: first.time_zone_id || null,
      raw: first,
    };
  } catch (error) {
    console.error('[addressApi] Failed to lookup zip:', error);
    return null;
  }
}


export async function fetchTimezone({ zip }) {
  if (!zip) return null;
  
  try {
    const response = await httpCall({
      url: `/1.0/timezone/${encodeURIComponent(zip)}`,
      method: 'GET',
    });
    
    return response || null;
  } catch (error) {
    console.error('[addressApi] Failed to fetch timezone:', error);
    return null;
  }
}
